import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness } from "@/App";
import { useNavigate } from "react-router-dom";
import { usePlaidLink } from "react-plaid-link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import BalanceHistory from "@/components/BalanceHistory";

const ACCOUNT_TYPES = [
  { value: "checking", label: "Checking Account", icon: "💳" },
  { value: "savings", label: "Savings Account", icon: "🏦" },
  { value: "business_checking", label: "Business Checking", icon: "💼" },
  { value: "business_savings", label: "Business Savings", icon: "📈" },
  { value: "money_market", label: "Money Market", icon: "💰" },
  { value: "credit_card", label: "Credit Card", icon: "💳" },
  { value: "profit", label: "Profit Account", icon: "🎯" },
  { value: "tax", label: "Tax Account", icon: "📋" },
  { value: "owner_pay", label: "Owner Pay Account", icon: "👤" },
  { value: "operating", label: "Operating Expenses", icon: "⚙️" },
];

// Component that auto-opens Plaid Link when token is available
const PlaidLinkOpener = ({ linkToken, onSuccess, onExit }) => {
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  useEffect(() => {
    if (ready && linkToken) {
      open();
    }
  }, [ready, linkToken, open]);

  return null;
};

const BankAccounts = () => {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("manual");
  const [manualAccounts, setManualAccounts] = useState([]);
  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [transactions, setTransactions] = useState({});
  const [accountTxnCounts, setAccountTxnCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [linkToken, setLinkToken] = useState(null);
  const [linking, setLinking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [plaidConfigured, setPlaidConfigured] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  
  // Manual account form
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    account_name: "",
    bank_name: "",
    account_type: "checking",
    account_number_last4: "",
    current_balance: "",
    purpose: "",
  });

  useEffect(() => {
    fetchAllAccounts();
  }, [selectedBusiness]);

  const fetchAllAccounts = async () => {
    setLoading(true);
    try {
      // Fetch manual accounts
      if (selectedBusiness) {
        const manualRes = await api.get(`/manual-bank-accounts/${selectedBusiness.id}`);
        setManualAccounts(manualRes.data);

        // Fetch transaction counts per account
        try {
          const txnRes = await api.get(`/transactions/${selectedBusiness.id}`);
          const counts = {};
          txnRes.data.forEach((t) => {
            counts[t.bank_account_id] = (counts[t.bank_account_id] || 0) + 1;
          });
          setAccountTxnCounts(counts);
        } catch (e) {
          console.error("Failed to fetch transaction counts:", e);
        }
      }
      
      // Try to fetch Plaid accounts
      try {
        const plaidRes = await api.get("/plaid/accounts");
        setPlaidAccounts(plaidRes.data);
        setPlaidConfigured(true);
      } catch (error) {
        if (error.response?.status === 400) {
          setPlaidConfigured(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const createLinkToken = async () => {
    try {
      const response = await api.post("/plaid/create-link-token", {
        business_id: selectedBusiness?.id
      });
      setLinkToken(response.data.link_token);
      return response.data.link_token;
    } catch (error) {
      if (error.response?.data?.detail?.includes("not configured")) {
        setPlaidConfigured(false);
        toast.error("Plaid is not configured. Please add PLAID_CLIENT_ID and PLAID_SECRET to continue.");
      } else {
        toast.error("Failed to initialize bank connection");
      }
      return null;
    }
  };

  const handleLinkAccount = async () => {
    setShowConsent(false);
    setLinking(true);
    const token = await createLinkToken();
    
    if (!token) {
      setLinking(false);
      return;
    }

    // Token is set in state, PlaidLinkButton will handle the rest
    setLinking(false);
  };

  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    try {
      setLinking(true);
      const response = await api.post("/plaid/exchange-token", {
        public_token: publicToken,
        business_id: selectedBusiness?.id,
        institution_id: metadata?.institution?.institution_id || null,
        institution_name: metadata?.institution?.name || null
      });
      const data = response.data;
      const msg = data.transactions_imported
        ? `Bank connected! ${data.accounts_count} account(s) linked, ${data.transactions_imported} transactions imported.`
        : `Bank account connected successfully!`;
      toast.success(msg);
      setLinkToken(null);
      await fetchAllAccounts();
    } catch (error) {
      console.error("Failed to exchange token:", error);
      toast.error(error.response?.data?.detail || "Failed to connect bank account");
    } finally {
      setLinking(false);
    }
  }, [selectedBusiness]);

  const onPlaidExit = useCallback((err, metadata) => {
    setLinkToken(null);
    if (err) {
      console.error("Plaid Link error:", err, metadata);
      const msg = err.display_message || err.error_message || "Bank connection failed. Please try again.";
      toast.error(msg);
    }
  }, []);

  const syncBalances = async () => {
    setSyncing(true);
    try {
      await api.post("/plaid/sync-balances");
      await fetchAllAccounts();
      toast.success("Account balances synced successfully!");
    } catch (error) {
      toast.error("Failed to sync balances");
    } finally {
      setSyncing(false);
    }
  };

  const syncTransactions = async () => {
    setSyncing(true);
    try {
      const response = await api.post("/plaid/sync-transactions");
      toast.success(`Synced ${response.data.added} new transactions!`);
    } catch (error) {
      toast.error("Failed to sync transactions");
    } finally {
      setSyncing(false);
    }
  };

  const fetchTransactions = async (accountId) => {
    setLoadingTransactions(true);
    try {
      const response = await api.get(`/plaid/transactions/${accountId}`);
      setTransactions(prev => ({ ...prev, [accountId]: response.data }));
    } catch (error) {
      toast.error("Failed to fetch transactions");
    } finally {
      setLoadingTransactions(false);
    }
  };

  const unlinkPlaidAccount = async (accountId) => {
    try {
      await api.delete(`/plaid/accounts/${accountId}`);
      toast.success("Account unlinked successfully");
      await fetchAllAccounts();
    } catch (error) {
      toast.error("Failed to unlink account");
    }
  };

  // Manual account CRUD
  const resetForm = () => {
    setAccountForm({
      account_name: "",
      bank_name: "",
      account_type: "checking",
      account_number_last4: "",
      current_balance: "",
      purpose: "",
    });
    setEditingAccount(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setAccountForm({
      account_name: account.account_name,
      bank_name: account.bank_name,
      account_type: account.account_type,
      account_number_last4: account.account_number_last4 || "",
      current_balance: account.current_balance?.toString() || "",
      purpose: account.purpose || "",
    });
    setShowAddDialog(true);
  };

  const saveManualAccount = async () => {
    if (!accountForm.account_name || !accountForm.bank_name) {
      toast.error("Please fill in account name and bank name");
      return;
    }

    try {
      const payload = {
        business_id: selectedBusiness.id,
        account_name: accountForm.account_name,
        bank_name: accountForm.bank_name,
        account_type: accountForm.account_type,
        account_number_last4: accountForm.account_number_last4 || null,
        current_balance: parseFloat(accountForm.current_balance) || 0,
        purpose: accountForm.purpose || null,
      };

      if (editingAccount) {
        await api.put(`/manual-bank-accounts/${editingAccount.id}`, payload);
        toast.success("Account updated successfully!");
      } else {
        await api.post("/manual-bank-accounts", payload);
        toast.success("Account added successfully!");
      }

      setShowAddDialog(false);
      resetForm();
      fetchAllAccounts();
    } catch (error) {
      toast.error("Failed to save account");
    }
  };

  const deleteManualAccount = async (accountId) => {
    try {
      await api.delete(`/manual-bank-accounts/${accountId}`);
      toast.success("Account deleted successfully");
      fetchAllAccounts();
    } catch (error) {
      toast.error("Failed to delete account");
    }
  };

  const updateBalance = async (accountId, newBalance) => {
    try {
      await api.put(`/manual-bank-accounts/${accountId}/balance`, {
        balance: parseFloat(newBalance)
      });
      toast.success("Balance updated!");
      fetchAllAccounts();
    } catch (error) {
      toast.error("Failed to update balance");
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getAccountTypeIcon = (type) => {
    const found = ACCOUNT_TYPES.find(t => t.value === type);
    return found?.icon || "💳";
  };

  const getAccountTypeLabel = (type) => {
    const found = ACCOUNT_TYPES.find(t => t.value === type);
    return found?.label || type;
  };

  const totalManualBalance = manualAccounts.reduce((sum, acc) => sum + (acc.current_balance || 0), 0);
  const totalPlaidBalance = plaidAccounts.reduce((sum, acc) => sum + (acc.current_balance || 0), 0);
  const totalBalance = totalManualBalance + totalPlaidBalance;

  if (!selectedBusiness) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Please select a business to manage bank accounts.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading accounts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="bank-accounts-page">
      {/* Plaid Link auto-opener */}
      {linkToken && (
        <PlaidLinkOpener
          linkToken={linkToken}
          onSuccess={onPlaidSuccess}
          onExit={onPlaidExit}
        />
      )}
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Bank Accounts</h1>
          <p className="text-stone-600">Manage your business bank accounts</p>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-[#1e3a5f]/5 to-stone-50 border-[#1e3a5f]/20">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 rounded-lg bg-white">
              <p className="text-sm text-stone-500 mb-1">Total Balance</p>
              <p className="text-3xl font-bold text-[#1e3a5f]">{formatCurrency(totalBalance)}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white">
              <p className="text-sm text-stone-500 mb-1">Manual Accounts</p>
              <p className="text-3xl font-bold text-blue-600">{manualAccounts.length}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white">
              <p className="text-sm text-stone-500 mb-1">Connected (Plaid)</p>
              <p className="text-3xl font-bold text-purple-600">{plaidAccounts.length}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white">
              <p className="text-sm text-stone-500 mb-1">Total Accounts</p>
              <p className="text-3xl font-bold text-stone-700">{manualAccounts.length + plaidAccounts.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Manual vs Plaid */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
          <TabsTrigger value="manual" data-testid="tab-manual">
            Manual Entry
            {manualAccounts.length > 0 && (
              <Badge variant="secondary" className="ml-2">{manualAccounts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="connected" data-testid="tab-connected">
            Connected (Plaid)
            {plaidAccounts.length > 0 && (
              <Badge variant="secondary" className="ml-2">{plaidAccounts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            Balance History
          </TabsTrigger>
        </TabsList>

        {/* Manual Accounts Tab */}
        <TabsContent value="manual" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <span>📝</span> Manual Bank Accounts
                  </CardTitle>
                  <CardDescription>
                    Add and track your bank accounts manually. Perfect for Starter plan users.
                  </CardDescription>
                </div>
                <Button
                  onClick={openAddDialog}
                  className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                  data-testid="add-manual-account-btn"
                >
                  + Add Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {manualAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
                    <span className="text-3xl">🏦</span>
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">No Manual Accounts Yet</h3>
                  <p className="text-stone-600 mb-6 max-w-md mx-auto">
                    Add your bank accounts manually to track balances and organize your Opturna Method accounts.
                  </p>
                  <Button onClick={openAddDialog} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
                    Add Your First Account
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {manualAccounts.map(account => (
                    <div
                      key={account.id}
                      className="p-4 rounded-lg border border-stone-200 hover:border-[#1e3a5f]/30 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{getAccountTypeIcon(account.account_type)}</div>
                          <div>
                            <h4 className="font-semibold text-stone-900">{account.account_name}</h4>
                            <p className="text-sm text-stone-500">
                              {account.bank_name}
                              {account.account_number_last4 && ` •••• ${account.account_number_last4}`}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {getAccountTypeLabel(account.account_type)}
                        </Badge>
                      </div>

                      <div className="p-3 rounded-lg bg-stone-50 mb-3">
                        <p className="text-xs text-stone-500 mb-1">Current Balance</p>
                        <p className="text-2xl font-bold text-[#1e3a5f]">
                          {formatCurrency(account.current_balance)}
                        </p>
                      </div>

                      {account.purpose && (
                        <p className="text-xs text-stone-500 mb-3 italic">Purpose: {account.purpose}</p>
                      )}

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/transactions?account=${account.id}`)}
                          data-testid={`view-txns-${account.id}`}
                        >
                          Transactions
                          {accountTxnCounts[account.id] > 0 && (
                            <Badge variant="secondary" className="ml-1.5 text-[10px]">{accountTxnCounts[account.id]}</Badge>
                          )}
                        </Button>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              Update Balance
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Update Balance</DialogTitle>
                              <DialogDescription>{account.account_name}</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>New Balance</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  defaultValue={account.current_balance}
                                  id={`balance-${account.id}`}
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button
                                onClick={() => {
                                  const input = document.getElementById(`balance-${account.id}`);
                                  updateBalance(account.id, input.value);
                                }}
                                className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                              >
                                Save Balance
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(account)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteManualAccount(account.id)}
                        >
                          Delete
                        </Button>
                      </div>

                      <p className="text-xs text-stone-400 mt-3">
                        Last updated: {new Date(account.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tips for Manual Entry */}
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-800 text-lg">💡 Tips for Manual Account Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
                <div className="flex gap-2">
                  <span>✓</span>
                  <span>Update balances on your allocation days (10th & 25th)</span>
                </div>
                <div className="flex gap-2">
                  <span>✓</span>
                  <span>Create separate accounts for each Opturna category</span>
                </div>
                <div className="flex gap-2">
                  <span>✓</span>
                  <span>Use the "purpose" field to note account goals</span>
                </div>
                <div className="flex gap-2">
                  <span>✓</span>
                  <span>Upgrade to Pro for automatic bank syncing</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Connected (Plaid) Tab */}
        <TabsContent value="connected" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <span>🔗</span> Connected Bank Accounts
                  </CardTitle>
                  <CardDescription>
                    Automatically sync balances and transactions with Plaid ({plaidAccounts.length}/5 accounts connected). You can link up to 5 accounts from one or multiple banks.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {plaidAccounts.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        onClick={syncBalances}
                        disabled={syncing}
                      >
                        {syncing ? "Syncing..." : "Sync Balances"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={syncTransactions}
                        disabled={syncing}
                      >
                        {syncing ? "Syncing..." : "Sync Transactions"}
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => setShowConsent(true)}
                    disabled={linking || plaidAccounts.length >= 5}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="link-bank-btn"
                  >
                    {linking ? "Connecting..." : plaidAccounts.length >= 5 ? "Limit Reached (5/5)" : "+ Link Bank"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!plaidConfigured && (
                <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <h4 className="font-semibold text-amber-800">Plaid Not Configured</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        To connect bank accounts automatically, Plaid credentials are required.
                        This feature is available on Pro ($99/mo) and Enterprise ($149/mo) plans.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {plaidAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-3xl">🔗</span>
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">No Connected Accounts</h3>
                  <p className="text-stone-600 mb-6 max-w-md mx-auto">
                    Connect up to 5 bank accounts from one or more banks. When you link a bank, all your accounts (checking, savings, credit cards) will be available to select at once.
                  </p>
                  <Button
                    onClick={() => setShowConsent(true)}
                    disabled={linking || !plaidConfigured}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="connect-first-account-btn"
                  >
                    Connect Your First Account
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {plaidAccounts.map(account => (
                    <div
                      key={account.id}
                      className="p-4 rounded-lg border border-purple-200 hover:border-purple-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-purple-100">
                            <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                              <path d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-semibold text-stone-900">{account.name}</h4>
                            <p className="text-sm text-stone-500">
                              {account.institution_name}
                              {account.mask && ` •••• ${account.mask}`}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700 capitalize">
                          {account.subtype || account.type}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="p-3 rounded-lg bg-stone-50">
                          <p className="text-xs text-stone-500 mb-1">Current</p>
                          <p className="text-xl font-bold text-stone-900">
                            {formatCurrency(account.current_balance)}
                          </p>
                        </div>
                        <div className="p-3 rounded-lg bg-stone-50">
                          <p className="text-xs text-stone-500 mb-1">Available</p>
                          <p className="text-xl font-bold text-[#1e3a5f]">
                            {formatCurrency(account.available_balance)}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                setSelectedAccount(account);
                                fetchTransactions(account.id);
                              }}
                            >
                              View Transactions
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Recent Transactions</DialogTitle>
                              <DialogDescription>{account.name} - {account.institution_name}</DialogDescription>
                            </DialogHeader>
                            <div className="mt-4">
                              {loadingTransactions ? (
                                <div className="text-center py-8 text-muted-foreground">Loading transactions...</div>
                              ) : transactions[account.id]?.length > 0 ? (
                                <div className="space-y-2">
                                  {transactions[account.id].slice(0, 20).map((txn, idx) => (
                                    <div key={txn.id || idx} className="flex justify-between items-center p-3 rounded-lg bg-stone-50">
                                      <div>
                                        <p className="font-medium text-stone-900">{txn.merchant_name || txn.name}</p>
                                        <p className="text-xs text-stone-500">
                                          {txn.date}
                                          {txn.pending && <Badge variant="secondary" className="ml-2">Pending</Badge>}
                                        </p>
                                      </div>
                                      <p className={`font-semibold ${txn.amount > 0 ? 'text-red-600' : 'text-[#1e3a5f]'}`}>
                                        {txn.amount > 0 ? '-' : '+'}${Math.abs(txn.amount).toFixed(2)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                  No transactions found. Try syncing transactions first.
                                </div>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => unlinkPlaidAccount(account.id)}
                        >
                          Unlink
                        </Button>
                      </div>

                      <p className="text-xs text-stone-400 mt-3">
                        Last synced: {account.last_synced ? new Date(account.last_synced).toLocaleString() : "Never"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Plaid Info */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>How Plaid Connection Works</CardTitle>
              <CardDescription>Securely connect your accounts with Plaid</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-xl font-bold text-purple-600">1</span>
                  </div>
                  <h4 className="font-semibold text-stone-900 mb-2">Connect</h4>
                  <p className="text-sm text-stone-600">
                    Click "Link Bank" and securely log in through Plaid's interface.
                  </p>
                </div>
                <div className="text-center p-4">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-xl font-bold text-purple-600">2</span>
                  </div>
                  <h4 className="font-semibold text-stone-900 mb-2">Sync</h4>
                  <p className="text-sm text-stone-600">
                    Balances and transactions are automatically synced daily.
                  </p>
                </div>
                <div className="text-center p-4">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-xl font-bold text-purple-600">3</span>
                  </div>
                  <h4 className="font-semibold text-stone-900 mb-2">Track</h4>
                  <p className="text-sm text-stone-600">
                    View all accounts in one place with real-time data.
                  </p>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-lg bg-blue-50 text-sm text-blue-800">
                <strong>Security Note:</strong> Plaid uses bank-level 256-bit encryption. Your credentials go directly to Plaid, never stored on our servers.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance History Tab */}
        <TabsContent value="history" className="mt-6">
          <BalanceHistory businessId={selectedBusiness?.id} />
        </TabsContent>
      </Tabs>

      {/* Add/Edit Manual Account Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAccount ? "Edit Account" : "Add Bank Account"}</DialogTitle>
            <DialogDescription>
              {editingAccount ? "Update your account details" : "Manually add a bank account to track"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Account Name *</Label>
              <Input
                placeholder="e.g., Business Checking"
                value={accountForm.account_name}
                onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })}
                data-testid="account-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Bank Name *</Label>
              <Input
                placeholder="e.g., Chase, Bank of America"
                value={accountForm.bank_name}
                onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })}
                data-testid="bank-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Account Type</Label>
              <Select
                value={accountForm.account_type}
                onValueChange={(value) => setAccountForm({ ...accountForm, account_type: value })}
              >
                <SelectTrigger data-testid="account-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Last 4 Digits (Optional)</Label>
              <Input
                placeholder="1234"
                maxLength={4}
                value={accountForm.account_number_last4}
                onChange={(e) => setAccountForm({ ...accountForm, account_number_last4: e.target.value.replace(/\D/g, '') })}
              />
            </div>
            <div className="space-y-2">
              <Label>Current Balance</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={accountForm.current_balance}
                onChange={(e) => setAccountForm({ ...accountForm, current_balance: e.target.value })}
                data-testid="balance-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Purpose / Notes (Optional)</Label>
              <Input
                placeholder="e.g., For tax savings"
                value={accountForm.purpose}
                onChange={(e) => setAccountForm({ ...accountForm, purpose: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={saveManualAccount} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
              {editingAccount ? "Update Account" : "Add Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Connect Consent Dialog */}
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bank-consent-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1e3a5f]">End User Consent</DialogTitle>
            <DialogDescription>Please review before connecting your bank account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-stone-700 leading-relaxed">
            <p>
              By selecting "Connect Bank Account," you authorize Opturna to use Plaid Inc. ("Plaid") to retrieve your financial account information from the institution(s) you choose to connect.
            </p>
            <div>
              <p className="font-semibold text-[#1e3a5f] mb-1.5">This may include:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Account balances</li>
                <li>Transaction history</li>
                <li>Account name and type</li>
                <li>Financial institution information</li>
              </ul>
            </div>
            <p>
              Opturna uses this information only to provide authorized features such as business finance tracking, cash flow insights, tax planning support, and related reporting.
            </p>
            <p>
              Opturna does not receive or store your online banking username or password. Those credentials are handled securely by Plaid and your financial institution.
            </p>
            <div>
              <p className="font-semibold text-[#1e3a5f] mb-1.5">You may disconnect your bank connection at any time by:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Navigating to Settings &gt; Connected Accounts</li>
                <li>Selecting "Disconnect Bank"</li>
                <li>Contacting <a href="mailto:admin@opturnatech.com" className="text-[#4a9eff] hover:underline">admin@opturnatech.com</a></li>
              </ul>
            </div>
            <div className="p-3 bg-[#f5f7fa] rounded-lg border border-[#e4e8ee]">
              <p className="text-xs text-[#5a6577] mb-2">By continuing, you acknowledge that you have read and agree to:</p>
              <ul className="space-y-1.5 ml-1">
                <li><a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#4a9eff] hover:underline text-xs">Opturna Privacy Policy</a></li>
                <li><a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#4a9eff] hover:underline text-xs">Opturna Terms of Service</a></li>
                <li><a href="/data-retention" target="_blank" rel="noopener noreferrer" className="text-[#4a9eff] hover:underline text-xs">Opturna Data Retention Policy</a></li>
                <li><a href="/security" target="_blank" rel="noopener noreferrer" className="text-[#4a9eff] hover:underline text-xs">Opturna Security Policy</a></li>
                <li><a href="https://plaid.com/legal/#consumers" target="_blank" rel="noopener noreferrer" className="text-[#4a9eff] hover:underline text-xs">Plaid Privacy Policy</a></li>
              </ul>
            </div>
            <label className="flex items-start gap-3 cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stone-300 text-[#1e3a5f] focus:ring-[#1e3a5f]"
                data-testid="consent-checkbox"
              />
              <span className="text-xs text-stone-600 leading-relaxed">
                I authorize Opturna to access the selected financial account information through Plaid for the purposes described above.
              </span>
            </label>
          </div>
          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowConsent(false); setConsentChecked(false); }} data-testid="consent-decline-btn">Decline</Button>
            <Button
              onClick={handleLinkAccount}
              disabled={!consentChecked}
              className="bg-[#1e3a5f] hover:bg-[#2d5a8e] disabled:opacity-50"
              data-testid="consent-agree-btn"
            >
              Connect Bank Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccounts;
